import React, { useState, useEffect, useMemo } from "react";
import { ProviderSetVersionResult } from "./jobStatisticsResult";
import OperationsService from "./operationsService";
import { ColumnConfig, Waffles } from "@app/shared/waffles";

interface ProviderSetVersionsFlyoutContentProps {
  clientId: number;
  createDate: Date;
  isActivated: boolean;
}

const operationsService = new OperationsService();

const ProviderSetVersionsFlyoutContent: React.FC<ProviderSetVersionsFlyoutContentProps> = ({ clientId, createDate, isActivated }) => {
  const [versionData, setVersionData] = useState<ProviderSetVersionResult[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const providerSetVersionColumns: ColumnConfig = {
    dateActivated: {
      displayText: "Date Activated",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-date-activated-output",
    },
    dateCreated: {
      displayText: "Date Created",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-date-created-output",
    },
    dateLastUpdated: {
      displayText: "Date Last Updated",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-date-last-updated-output",
    },
    name: {
      displayText: "Name",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-name-output",
    },
    providerSetId: {
      displayText: "Provider Set ID",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-id-output",
    },
    providerSetVersionId: {
      displayText: "Version ID",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-id-output",
    },
    providerSetVersionStatus: {
      displayText: "Version Status",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-status-output",
    },
    totalAccessPoints: {
      displayText: "Total Access Points",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-total-access-points-output",
    },
    uniqueLocations: {
      displayText: "Unique Locations",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-unique-locations-output",
    },
    uniqueProviders: {
      displayText: "Unique Providers",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-version-unique-providers-output",
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await operationsService.getProviderSetVersions(
          clientId,
          createDate.toISOString(),
          isActivated
        );
        setVersionData(result);
      } catch (err) {
        setError("Error fetching provider set versions");
        console.error("Error fetching provider set versions", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clientId, createDate, isActivated]);

  const tableData = useMemo(() => {
    return versionData.map((version) => ({
      /* eslint-disable sort-keys */
      key: version.providerSetVersionId.toString(),
      name: version.name,
      providerSetVersionId: version.providerSetVersionId,
      providerSetId: version.providerSetId,
      dateCreated: new Date(version.dateCreated).toLocaleDateString(),
      dateLastUpdated: version.dateLastUpdated ? new Date(version.dateLastUpdated).toLocaleDateString() : "N/A",
      dateActivated: version.dateActivated ? new Date(version.dateActivated).toLocaleDateString() : "N/A",
      providerSetVersionStatus: version.providerSetVersionStatus,
      totalAccessPoints: version.totalAccessPoints,
      uniqueProviders: version.uniqueProviders,
      uniqueLocations: version.uniqueLocations,
      /* eslint-enable sort-keys */
    }));
  }, [versionData]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div>
      <Waffles
        columns={providerSetVersionColumns}
        data={tableData}
        enablePagination={false}
        passThroughSorting={true}
        testId="weekend-run-monitoring__provider-set-version-table"
      />
    </div>
  );
};

export default ProviderSetVersionsFlyoutContent;